import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { requestIdleCallback } from '@theme/utilities';

const RECOMMENDATIONS_SECTION_ID = 'upsell-recommendations';

// sections/upsell-recommendations.liquid renders every product it gets back (its
// own `limit: 9`), so this is the card count, not a buffer over a smaller grid.
// The section drops the added product and any sold-out variant, so the modal
// shows up to 9 and typically 8.
const RECOMMENDATIONS_LIMIT = 9;

/**
 * @typedef {object} CartItem
 * @property {number} product_id
 * @property {number} variant_id
 * @property {string} handle
 * @property {string} product_title
 * @property {string | null} image
 * @property {number} final_price
 */

/**
 * @typedef {object} AnalyticsItem
 * @property {string | number} [productId]
 * @property {string | number} [variantId]
 * @property {string} [productHandle]
 */

/**
 * A custom element that confirms cart additions and offers related products.
 *
 * @typedef {object} UpsellModalRefs
 * @property {HTMLDialogElement} dialog - The modal dialog.
 * @property {HTMLImageElement} confirmationImage - The added product image.
 * @property {HTMLElement} confirmationTitle - The added product title.
 * @property {HTMLElement} confirmationPrice - The added product price.
 * @property {HTMLElement} recommendations - The recommendation region.
 * @property {HTMLElement} recommendationsContent - The server-rendered recommendation cards.
 *
 * @extends {DialogComponent}
 */
class UpsellModalComponent extends DialogComponent {
  requiredRefs = [
    'dialog',
    'confirmationImage',
    'confirmationTitle',
    'confirmationPrice',
    'recommendations',
    'recommendationsContent',
  ];

  /** In-flight or settled recommendation HTML, keyed by request URL. @type {Map<string, Promise<string>>} */
  #recommendationsCache = new Map();

  /** The URL whose response is allowed to render right now. @type {string | null} */
  #currentRecommendationsUrl = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    const { productId } = this.dataset;
    if (productId) requestIdleCallback(() => this.#warmRecommendations(productId));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Handles successful product-form additions.
   * @param {Event} event
   */
  #handleCartAdd = async (event) => {
    if (!(event instanceof CartAddEvent) || event.detail.data.didError) return;

    // Quick-order quantity changes fire CartAddEvent even on decreases —
    // they are line edits, not "added to cart" moments.
    const source = event.detail.data.source;
    if (source === 'quick-order-quantity' || source === 'quick-order-remove-all') return;

    if (event.target instanceof Element && this.contains(event.target)) {
      this.#handleRecommendationAdded(event);
      return;
    }

    // Adds from a grid card's corner button (or its choose dialog) stay quiet:
    // no upsell interruption while browsing the grid (SOO-58). The cart cue
    // and count still update.
    if (event.target instanceof Element && event.target.closest('quick-add-component, #quick-add-dialog')) return;

    const item = await this.#findAddedItem(event);
    if (!item) return;

    this.#renderConfirmation(item);

    await this.#waitForQuickAddToClose();
    this.showDialog();
    this.#capture('upsell_modal_viewed', {
      product_handle: item.handle,
      ...this.#shippingMeterProperties(),
    });
    this.#loadRecommendations(item.product_id);
  };

  /**
   * Finds the just-added line item in the cart payload.
   * @param {CartAddEvent} event
   * @returns {Promise<CartItem | undefined>}
   */
  async #findAddedItem(event) {
    const resource = /** @type {{ items?: CartItem[] } | undefined} */ (event.detail.resource);
    let items = resource?.items;
    if (!items?.length) {
      try {
        const response = await fetch(this.dataset.cartJsonUrl || '/cart.js');
        if (response.ok) {
          const cart = await response.json();
          items = cart.items;
        }
      } catch (error) {
        console.error('Upsell cart confirmation error:', error);
      }
    }

    if (!items?.length) return undefined;

    const analyticsItems = /** @type {AnalyticsItem[] | undefined} */ (event.detail.data.analyticsItems);
    const analyticsItem = analyticsItems?.[0];
    const variantId = analyticsItem?.variantId ?? event.detail.data.variantId;
    const productId = analyticsItem?.productId ?? event.detail.data.productId;

    return (
      items.find((item) => variantId && item.variant_id.toString() === variantId.toString()) ??
      items.find((item) => productId && item.product_id.toString() === productId.toString()) ??
      items[0]
    );
  }

  /**
   * Updates the confirmation content from the cart line item.
   * @param {CartItem} item
   */
  #renderConfirmation(item) {
    const confirmationImage = /** @type {HTMLImageElement} */ (this.refs.confirmationImage);
    const confirmationTitle = /** @type {HTMLElement} */ (this.refs.confirmationTitle);
    const confirmationPrice = /** @type {HTMLElement} */ (this.refs.confirmationPrice);

    confirmationTitle.textContent = item.product_title;
    confirmationPrice.textContent = formatMoney(
      item.final_price,
      this.dataset.moneyFormat || '{{amount}}',
      this.dataset.currency || 'USD',
    );

    if (item.image) {
      confirmationImage.src = item.image;
      confirmationImage.alt = item.product_title;
      confirmationImage.hidden = false;
    } else {
      confirmationImage.removeAttribute('src');
      confirmationImage.alt = '';
      confirmationImage.hidden = true;
    }
  }

  /**
   * Waits for the theme's quick-add dialog to finish its normal cart-add close.
   * @returns {Promise<void>}
   */
  #waitForQuickAddToClose() {
    const quickAddDialog = document.querySelector('#quick-add-dialog');
    const dialog = quickAddDialog?.querySelector('dialog');
    if (!(quickAddDialog instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement) || !dialog.open) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      quickAddDialog.addEventListener(DialogCloseEvent.eventName, () => resolve(), { once: true });
    });
  }

  /**
   * Builds the Section Rendering API URL for a product's related products.
   * @param {number | string} productId
   * @returns {string}
   */
  #recommendationsUrl(productId) {
    return `${this.dataset.recommendationsUrl}?product_id=${productId}&limit=${RECOMMENDATIONS_LIMIT}&intent=related&section_id=${RECOMMENDATIONS_SECTION_ID}`;
  }

  /**
   * Fetches recommendation HTML, de-duplicated per URL. The promise — not the
   * resolved text — is cached, so an open that lands mid-warm awaits the same
   * request instead of firing a second one.
   * @param {string} url
   * @returns {Promise<string>}
   */
  #fetchRecommendations(url) {
    let pending = this.#recommendationsCache.get(url);
    if (!pending) {
      pending = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return response.text();
      });
      // Failures aren't cached — a later open retries rather than reusing the error.
      pending.catch(() => this.#recommendationsCache.delete(url));
      this.#recommendationsCache.set(url, pending);
    }
    return pending;
  }

  /**
   * Pre-fetches this page's own recommendations at idle so the modal opens with
   * cards already in hand. Never touches the modal DOM; failures are silent.
   * @param {string} productId
   */
  async #warmRecommendations(productId) {
    try {
      const html = await this.#fetchRecommendations(this.#recommendationsUrl(productId));
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const images = documentFragment.querySelectorAll('[data-upsell-recommendation] img');

      // ponytail: the first row is what's above the fold on open; the rest can load normally.
      for (const image of Array.from(images).slice(0, 3)) {
        if (!(image instanceof HTMLImageElement)) continue;
        const warm = new Image();
        if (image.sizes) warm.sizes = image.sizes;
        if (image.srcset) warm.srcset = image.srcset;
        warm.src = image.src;
      }
    } catch {
      // Best-effort: a failed warm just means the open path fetches as before.
    }
  }

  /**
   * Loads related products through Shopify's recommendations endpoint.
   * @param {number} productId
   */
  async #loadRecommendations(productId) {
    const recommendations = /** @type {HTMLElement} */ (this.refs.recommendations);
    const recommendationsContent = /** @type {HTMLElement} */ (this.refs.recommendationsContent);
    const url = this.#recommendationsUrl(productId);

    recommendations.hidden = true;
    recommendationsContent.replaceChildren();
    this.#currentRecommendationsUrl = url;

    try {
      const html = await this.#fetchRecommendations(url);

      // A newer add-to-cart took over while this was in flight — that render owns
      // the modal now, so a stale response must not overwrite it.
      if (this.#currentRecommendationsUrl !== url) return;

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const renderedRecommendations = documentFragment.querySelector('[data-upsell-recommendations]');
      if (!renderedRecommendations?.querySelector('[data-upsell-recommendation]')) return;

      recommendationsContent.replaceChildren(renderedRecommendations);
      recommendations.hidden = false;
    } catch (error) {
      console.error('Upsell recommendations error:', error);
    }
  }

  /**
   * Marks a recommendation as added without replacing the modal content.
   * @param {CartAddEvent} event
   */
  #handleRecommendationAdded(event) {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('[data-upsell-recommendation]');
    const button = card?.querySelector('[ref="addToCartButton"]');
    const label = button?.querySelector('.add-to-cart-text__content');

    if (button instanceof HTMLButtonElement) button.disabled = true;
    if (label instanceof HTMLElement) label.textContent = `${this.dataset.addedLabel} ✓`;

    const analyticsItems = /** @type {AnalyticsItem[] | undefined} */ (event.detail.data.analyticsItems);
    const analyticsItem = analyticsItems?.[0];
    const productHandle = analyticsItem?.productHandle ?? card?.getAttribute('data-product-handle');
    this.#capture('upsell_modal_item_added', { product_handle: productHandle });
  }

  /**
   * Closes the upsell modal and opens the existing cart drawer.
   */
  viewCart = async () => {
    this.#capture('upsell_modal_view_cart_clicked');
    await this.closeDialog();

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    window.location.assign(this.dataset.cartUrl || '/cart');
  };

  /**
   * Reads the free-shipping meter rendered inside this modal so the view event
   * can be segmented by gap zone. `soodam-shipping-progress.js` repaints every
   * meter synchronously on the same `cart:update` that precedes the (async)
   * open, so the dataset is current here. Cents are in the cart's presentment
   * currency — thresholds differ per market, nothing is converted.
   * @returns {{ shipping_meter_state?: string, shipping_meter_remaining_cents?: number }}
   */
  #shippingMeterProperties() {
    const meter = this.querySelector('[data-shipping-progress]');
    if (!(meter instanceof HTMLElement)) return {};

    const { state, remainingCents } = meter.dataset;
    const remaining = Number(remainingCents);
    return {
      ...(state ? { shipping_meter_state: state } : {}),
      ...(remainingCents && Number.isFinite(remaining)
        ? { shipping_meter_remaining_cents: remaining }
        : {}),
    };
  }

  /**
   * Captures a PostHog event when analytics is available.
   * @param {string} eventName
   * @param {Record<string, unknown>} [properties]
   */
  #capture(eventName, properties = {}) {
    const posthog =
      /** @type {{ capture: (name: string, properties: Record<string, unknown>) => void } | undefined} */ (
        Reflect.get(window, 'posthog')
      );
    posthog?.capture(eventName, {
      ...properties,
      pathname: window.location.pathname,
    });
  }
}

if (!customElements.get('upsell-modal-component')) {
  customElements.define('upsell-modal-component', UpsellModalComponent);
}
